import { useForm } from "@tanstack/react-form";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { apiErrorHandler } from "@/core/api/api-error-handler";
import { MutationConfirmPreview } from "@/core/components/shared/preview-action";
import { Button } from "@/core/components/ui/button";
import { ButtonSpinner } from "@/core/components/ui/buttonStyled/ButtonSpinner.component";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/core/components/ui/dialog";
import { EntitySearchableSelect } from "@/core/components/ui/entity-searchable-select";
import { Input } from "@/core/components/ui/input";
import { Label } from "@/core/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/core/components/ui/select";
import useTypedTranslation from "@/core/hooks/use-typed-translation";
import {
  DEFAULT_CURRENCY,
  minorToMajor,
} from "@/core/utils/func/money-minor";
import {
  datetimeLocalToIso,
  toDatetimeLocalInput,
} from "@/core/utils/func/format-date";
import { notifSilence, notifSilenceError } from "@/core/utils/func/notifApp";
import type { SimulateWalletTransactionCompletedRequest } from "@/features/notification/api/dto";
import { useSimulateTransactionCompleted } from "@/features/notification/api/use-simulate-transaction-completed";
import { useNotificationsTranslation } from "@/features/notification/i18n/use-notifications-translation";
import {
  createSimulateTransactionCompletedSchema,
  type SimulateTransactionCompletedFormValues,
} from "@/features/notification/schemas/simulate-event.schema";
import { PREFERRED_LANGUAGES } from "@/features/shared/api/enums";
import {
  usePartySelectOptions,
  useProductCodeCodeSelectOptions,
  useTransactionReferenceSelectOptions,
} from "@/features/shared/api";

const EMPTY_VALUES: SimulateTransactionCompletedFormValues = {
  eventId: "",
  transactionReference: "",
  productCode: "",
  partyId: "",
  recipientAddress: "",
  preferredLanguage: "FR",
  agentPartyId: "",
  agentRecipientAddress: "",
  agentPreferredLanguage: "",
  amountMinor: 0,
  feeAmountMinor: 0,
  agentCommissionAmountMinor: 0,
  currency: DEFAULT_CURRENCY,
  completedAt: "",
};

const freshValues = (): SimulateTransactionCompletedFormValues => ({
  ...EMPTY_VALUES,
  eventId: crypto.randomUUID(),
  completedAt: toDatetimeLocalInput(),
});

const toPayload = (
  values: SimulateTransactionCompletedFormValues
): SimulateWalletTransactionCompletedRequest => ({
  eventId: values.eventId.trim(),
  transactionReference: values.transactionReference.trim(),
  productCode: values.productCode.trim(),
  partyId: values.partyId.trim(),
  recipientAddress: values.recipientAddress.trim(),
  preferredLanguage: values.preferredLanguage,
  agentPartyId: values.agentPartyId.trim() || undefined,
  agentRecipientAddress: values.agentRecipientAddress.trim() || undefined,
  agentPreferredLanguage: values.agentPreferredLanguage.trim() || undefined,
  amountMinor: values.amountMinor,
  feeAmountMinor: values.feeAmountMinor,
  agentCommissionAmountMinor: values.agentCommissionAmountMinor || undefined,
  currency: values.currency.trim(),
  completedAt: datetimeLocalToIso(values.completedAt.trim()),
});

/**
 * Dialog `simulate-wallet-transaction-completed` — diffusion en éventail
 * (client + éventuellement agent). Identifiants : selects searchables
 * (`POST /Parties/all`, `POST /Transactions/all` via la référence métier,
 * `POST /ProductCodes/all` via le code). `eventId` : UUID client.
 */
export function SimulateTransactionCompletedDialog({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useTypedTranslation();
  const { t: tNotif } = useNotificationsTranslation();
  const { i18n } = useTranslation();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- i18n.language : dépendance voulue, le schéma zod traduit est reconstruit à chaque changement de langue (convention login.schema.ts).
  const schema = useMemo(() => createSimulateTransactionCompletedSchema(), [i18n.language]);

  const simulate = useSimulateTransactionCompleted();
  const pending = simulate.isPending;
  const [preview, setPreview] =
    useState<SimulateWalletTransactionCompletedRequest | null>(null);
  const { options: partyOptions, isLoading: partiesLoading } =
    usePartySelectOptions(open);
  const { options: transactionOptions, isLoading: transactionsLoading } =
    useTransactionReferenceSelectOptions(open);
  const { options: productCodeOptions, isLoading: productCodesLoading } =
    useProductCodeCodeSelectOptions(open);

  const form = useForm({
    defaultValues: EMPTY_VALUES,
    onSubmit: async ({ value }) => {
      const parsed = schema.safeParse(value);
      if (!parsed.success) return;
      setPreview(toPayload(parsed.data));
    },
  });

  useEffect(() => {
    if (!open) {
      setPreview(null);
      form.reset(EMPTY_VALUES);
      return;
    }
    form.reset(freshValues());
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reset ciblé à l'ouverture.
  }, [open]);

  const fieldError =
    (name: keyof SimulateTransactionCompletedFormValues) =>
    (props: { value: unknown }) => {
      const shape = schema.shape[name];
      const result = shape.safeParse(props.value ?? "");
      return result.success ? undefined : result.error.issues[0]?.message;
    };

  const handleConfirm = () => {
    if (!preview) return;
    simulate.mutate(preview, {
      onSuccess: () => {
        notifSilence(tNotif("notifications.events.feedback.sent"));
        setPreview(null);
        onOpenChange(false);
      },
      onError: (error) => notifSilenceError(apiErrorHandler(error)),
    });
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-h-[90vh] sm:max-w-4xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {tNotif("notifications.events.transactionCompleted.title")}
            </DialogTitle>
            <DialogDescription>
              {tNotif("notifications.events.warning")}
            </DialogDescription>
          </DialogHeader>
          <form
            className="grid grid-cols-1 gap-4 sm:grid-cols-2"
            onSubmit={(e) => {
              e.preventDefault();
              e.stopPropagation();
              void form.handleSubmit();
            }}
          >
            <form.Field name="eventId" validators={{ onChange: fieldError("eventId") }}>
              {(field) => (
                <div className="space-y-1.5 sm:col-span-2">
                  <Label htmlFor="tc-eventId">
                    {tNotif("notifications.events.common.eventId")}
                  </Label>
                  <Input
                    id="tc-eventId"
                    value={field.state.value}
                    placeholder={tNotif("notifications.events.common.eventIdPlaceholder")}
                    className="notranslate font-mono"
                    onBlur={field.handleBlur}
                    onChange={(e) => field.handleChange(e.target.value)}
                  />
                  {field.state.meta.errors.length > 0 && (
                    <p className="text-sm text-destructive">
                      {field.state.meta.errors[0]}
                    </p>
                  )}
                </div>
              )}
            </form.Field>

            <form.Field
              name="transactionReference"
              validators={{ onChange: fieldError("transactionReference") }}
            >
              {(field) => (
                <div className="space-y-1.5 sm:col-span-2">
                  <Label htmlFor="tc-txref">
                    {tNotif(
                      "notifications.events.transactionCompleted.transactionReference"
                    )}
                  </Label>
                  <EntitySearchableSelect
                    id="tc-txref"
                    options={transactionOptions}
                    value={field.state.value}
                    onValueChange={(value) => field.handleChange(value)}
                    placeholder={tNotif(
                      "notifications.events.transactionCompleted.transactionReferencePlaceholder"
                    )}
                    isLoading={transactionsLoading}
                  />
                  {field.state.meta.errors.length > 0 && (
                    <p className="text-sm text-destructive">
                      {field.state.meta.errors[0]}
                    </p>
                  )}
                </div>
              )}
            </form.Field>

            <form.Field
              name="productCode"
              validators={{ onChange: fieldError("productCode") }}
            >
              {(field) => (
                <div className="space-y-1.5 sm:col-span-2">
                  <Label htmlFor="tc-product">
                    {tNotif("notifications.events.transactionCompleted.productCode")}
                  </Label>
                  <EntitySearchableSelect
                    id="tc-product"
                    options={productCodeOptions}
                    value={field.state.value}
                    onValueChange={(value) => field.handleChange(value)}
                    placeholder={tNotif(
                      "notifications.events.transactionCompleted.productCodePlaceholder"
                    )}
                    isLoading={productCodesLoading}
                  />
                  {field.state.meta.errors.length > 0 && (
                    <p className="text-sm text-destructive">
                      {field.state.meta.errors[0]}
                    </p>
                  )}
                </div>
              )}
            </form.Field>

            <form.Field name="partyId" validators={{ onChange: fieldError("partyId") }}>
              {(field) => (
                <div className="space-y-1.5 sm:col-span-2">
                  <Label htmlFor="tc-partyId">
                    {tNotif("notifications.events.common.partyId")}
                  </Label>
                  <EntitySearchableSelect
                    id="tc-partyId"
                    options={partyOptions}
                    value={field.state.value}
                    onValueChange={(value) => field.handleChange(value)}
                    placeholder={tNotif("notifications.events.common.partyIdPlaceholder")}
                    isLoading={partiesLoading}
                  />
                  {field.state.meta.errors.length > 0 && (
                    <p className="text-sm text-destructive">
                      {field.state.meta.errors[0]}
                    </p>
                  )}
                </div>
              )}
            </form.Field>

            <form.Field
              name="recipientAddress"
              validators={{ onChange: fieldError("recipientAddress") }}
            >
              {(field) => (
                <div className="space-y-1.5">
                  <Label htmlFor="tc-recipient">
                    {tNotif("notifications.events.common.recipientAddress")}
                  </Label>
                  <Input
                    id="tc-recipient"
                    className="notranslate"
                    value={field.state.value}
                    placeholder={tNotif(
                      "notifications.events.common.recipientAddressPlaceholder"
                    )}
                    onBlur={field.handleBlur}
                    onChange={(e) => field.handleChange(e.target.value)}
                  />
                  {field.state.meta.errors.length > 0 && (
                    <p className="text-sm text-destructive">
                      {field.state.meta.errors[0]}
                    </p>
                  )}
                </div>
              )}
            </form.Field>

            <form.Field
              name="preferredLanguage"
              validators={{ onChange: fieldError("preferredLanguage") }}
            >
              {(field) => (
                <div className="space-y-1.5">
                  <Label>{tNotif("notifications.events.common.preferredLanguage")}</Label>
                  <Select
                    value={field.state.value}
                    onValueChange={(v) =>
                      field.handleChange(
                        v as SimulateTransactionCompletedFormValues["preferredLanguage"]
                      )
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {PREFERRED_LANGUAGES.map((v) => (
                        <SelectItem key={v} value={v} className="notranslate">
                          {v}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </form.Field>

            <form.Field
              name="agentPartyId"
              validators={{ onChange: fieldError("agentPartyId") }}
            >
              {(field) => (
                <div className="space-y-1.5 sm:col-span-2">
                  <Label htmlFor="tc-agent-partyId">
                    {tNotif("notifications.events.transactionCompleted.agentPartyId")}
                  </Label>
                  <EntitySearchableSelect
                    id="tc-agent-partyId"
                    options={partyOptions}
                    value={field.state.value}
                    onValueChange={(value) => field.handleChange(value)}
                    placeholder={tNotif(
                      "notifications.events.transactionCompleted.agentPartyIdPlaceholder"
                    )}
                    isLoading={partiesLoading}
                    clearable
                  />
                  {field.state.meta.errors.length > 0 && (
                    <p className="text-sm text-destructive">
                      {field.state.meta.errors[0]}
                    </p>
                  )}
                </div>
              )}
            </form.Field>

            <form.Field
              name="agentRecipientAddress"
              validators={{ onChange: fieldError("agentRecipientAddress") }}
            >
              {(field) => (
                <div className="space-y-1.5">
                  <Label htmlFor="tc-agent-recipient">
                    {tNotif(
                      "notifications.events.transactionCompleted.agentRecipientAddress"
                    )}
                  </Label>
                  <Input
                    id="tc-agent-recipient"
                    className="notranslate"
                    value={field.state.value}
                    placeholder={tNotif(
                      "notifications.events.transactionCompleted.agentRecipientAddressPlaceholder"
                    )}
                    onBlur={field.handleBlur}
                    onChange={(e) => field.handleChange(e.target.value)}
                  />
                </div>
              )}
            </form.Field>

            <form.Field
              name="agentPreferredLanguage"
              validators={{ onChange: fieldError("agentPreferredLanguage") }}
            >
              {(field) => (
                <div className="space-y-1.5">
                  <Label htmlFor="tc-agent-lang">
                    {tNotif(
                      "notifications.events.transactionCompleted.agentPreferredLanguage"
                    )}
                  </Label>
                  <Input
                    id="tc-agent-lang"
                    className="notranslate"
                    value={field.state.value}
                    placeholder={tNotif(
                      "notifications.events.transactionCompleted.agentPreferredLanguagePlaceholder"
                    )}
                    onBlur={field.handleBlur}
                    onChange={(e) => field.handleChange(e.target.value)}
                  />
                </div>
              )}
            </form.Field>

            <form.Field
              name="amountMinor"
              validators={{ onChange: fieldError("amountMinor") }}
            >
              {(field) => (
                <div className="space-y-1.5">
                  <Label htmlFor="tc-amount">
                    {tNotif("notifications.events.transactionCompleted.amountMinor")}
                  </Label>
                  <Input
                    id="tc-amount"
                    type="number"
                    min={0}
                    step={1}
                    className="notranslate"
                    placeholder={tNotif(
                      "notifications.events.transactionCompleted.amountMinorPlaceholder"
                    )}
                    value={String(field.state.value)}
                    onBlur={field.handleBlur}
                    onChange={(e) =>
                      field.handleChange(Number(e.target.value) || 0)
                    }
                  />
                  {field.state.meta.errors.length > 0 && (
                    <p className="text-sm text-destructive">
                      {field.state.meta.errors[0]}
                    </p>
                  )}
                </div>
              )}
            </form.Field>

            <form.Field
              name="feeAmountMinor"
              validators={{ onChange: fieldError("feeAmountMinor") }}
            >
              {(field) => (
                <div className="space-y-1.5">
                  <Label htmlFor="tc-fee">
                    {tNotif("notifications.events.transactionCompleted.feeAmountMinor")}
                  </Label>
                  <Input
                    id="tc-fee"
                    type="number"
                    min={0}
                    step={1}
                    className="notranslate"
                    placeholder={tNotif(
                      "notifications.events.transactionCompleted.feeAmountMinorPlaceholder"
                    )}
                    value={String(field.state.value)}
                    onBlur={field.handleBlur}
                    onChange={(e) =>
                      field.handleChange(Number(e.target.value) || 0)
                    }
                  />
                  {field.state.meta.errors.length > 0 && (
                    <p className="text-sm text-destructive">
                      {field.state.meta.errors[0]}
                    </p>
                  )}
                </div>
              )}
            </form.Field>

            <form.Field
              name="agentCommissionAmountMinor"
              validators={{ onChange: fieldError("agentCommissionAmountMinor") }}
            >
              {(field) => (
                <div className="space-y-1.5">
                  <Label htmlFor="tc-commission">
                    {tNotif(
                      "notifications.events.transactionCompleted.agentCommissionAmountMinor"
                    )}
                  </Label>
                  <Input
                    id="tc-commission"
                    type="number"
                    min={0}
                    step={1}
                    className="notranslate"
                    placeholder={tNotif(
                      "notifications.events.transactionCompleted.agentCommissionAmountMinorPlaceholder"
                    )}
                    value={String(field.state.value)}
                    onBlur={field.handleBlur}
                    onChange={(e) =>
                      field.handleChange(Number(e.target.value) || 0)
                    }
                  />
                </div>
              )}
            </form.Field>

            <form.Field
              name="currency"
              validators={{ onChange: fieldError("currency") }}
            >
              {(field) => (
                <div className="space-y-1.5">
                  <Label htmlFor="tc-currency">
                    {tNotif("notifications.events.transactionCompleted.currency")}
                  </Label>
                  <Input
                    id="tc-currency"
                    value={field.state.value}
                    placeholder={tNotif(
                      "notifications.events.transactionCompleted.currencyPlaceholder"
                    )}
                    maxLength={3}
                    className="notranslate uppercase"
                    onBlur={field.handleBlur}
                    onChange={(e) => field.handleChange(e.target.value.toUpperCase())}
                  />
                  {field.state.meta.errors.length > 0 && (
                    <p className="text-sm text-destructive">
                      {field.state.meta.errors[0]}
                    </p>
                  )}
                </div>
              )}
            </form.Field>

            <form.Field
              name="completedAt"
              validators={{ onChange: fieldError("completedAt") }}
            >
              {(field) => (
                <div className="space-y-1.5 sm:col-span-2">
                  <Label htmlFor="tc-completedAt">
                    {tNotif("notifications.events.transactionCompleted.completedAt")}
                  </Label>
                  <Input
                    id="tc-completedAt"
                    type="datetime-local"
                    value={field.state.value}
                    className="notranslate"
                    onBlur={field.handleBlur}
                    onChange={(e) => field.handleChange(e.target.value)}
                  />
                  {field.state.meta.errors.length > 0 && (
                    <p className="text-sm text-destructive">
                      {field.state.meta.errors[0]}
                    </p>
                  )}
                </div>
              )}
            </form.Field>

            <DialogFooter className="sm:col-span-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={pending}
              >
                {t("common.cancel")}
              </Button>
              <Button type="submit" disabled={pending}>
                {pending ? <ButtonSpinner /> : null}
                {tNotif("notifications.events.confirm.action")}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
      <MutationConfirmPreview
        open={preview !== null}
        onOpenChange={(isOpen) => {
          if (!isOpen) setPreview(null);
        }}
        data={preview}
        title={tNotif("notifications.events.confirm.title")}
        description={tNotif("notifications.events.confirm.description")}
        t={tNotif}
        confirmLabel={tNotif("notifications.events.confirm.action")}
        pending={pending}
        fees={
          preview
            ? [
                {
                  label: tNotif(
                    "notifications.events.transactionCompleted.feeAmountMinor"
                  ),
                  amount: minorToMajor(preview.feeAmountMinor, preview.currency),
                  currency: preview.currency,
                },
              ]
            : undefined
        }
        total={
          preview
            ? {
                label: tNotif(
                  "notifications.events.transactionCompleted.amountMinor"
                ),
                amount: minorToMajor(preview.amountMinor, preview.currency),
                currency: preview.currency,
              }
            : undefined
        }
        onConfirm={handleConfirm}
      />
    </>
  );
}
